<template>
  <div>
    <FormRow v-if="hasFieldList" label="Field">
      <div class="row no-gutters" v-if="editing">
        <div class="col-10">
          <FormSelect :options="fieldTypeLookup" :value="fieldSettingKey" @input="fieldTypeUpdated" />
        </div>
        <div class="col-2 text-right">
          <FormButton class="btn-med btn-default" @click="removeFieldClicked"><i class="far fa-trash-alt"></i> Remove Field</FormButton>
        </div>
      </div>
      <FormStaticLabel v-if="!editing">{{formatLookupName(`${fieldSettings.model}.${fieldSettings.field}`, fieldTypeLookup)}}</FormStaticLabel>
    </FormRow>

    <FormRow v-if="hasFieldTypeSelected && isFieldModelNull" label="Source Value">
      <FormInputText v-model="fieldSettings.sourceValue" @input="fieldUpdated" v-if="editing" ref="inputSourceTextField" @focus="$emit('sourceFieldFocus', $event)" />
      <FormStaticLabel v-if="!editing">{{fieldSettings.sourceValue}}</FormStaticLabel>
    </FormRow>

    <FormRow v-if="hasFieldTypeSelected" label="Condition">
      <FormSelect v-if="editing" :options="fieldCompareLookup" v-model="fieldSettings.compareType" @input="fieldUpdated" />
      <FormStaticLabel v-if="!editing">{{formatLookupName(fieldSettings.compareType, fieldCompareLookup)}}</FormStaticLabel>
    </FormRow>

    <FormRow v-if="editing && hasFieldTypeSelected && hasFieldCompareValue" label="Values">
      <FormSelect
        :options="fieldValueLookup"
        v-if="isFieldInfoTypeBoolean"
        v-model="fieldSettings.value"
        @input="fieldUpdated" 
        :note="fieldNote"
      />

      <FormMultiSelect
        :options="fieldValueLookup"
        v-if="isFieldInfoTypeEnum"
        v-model="fieldSettings.value"
        @input="fieldUpdated" 
        :note="fieldNote"
      />

      <AutomationDynamicListInput
        v-if="isFieldInfoTypeDynamicList"
        v-model="fieldSettings.value"
        :linkType="fieldLinkType"
        @updateInfo="dynamicListFieldInfoUpdated"
        :editable="true"
        :note="fieldNote"
      />

      <FormInputText
        type="number"
        ref="inputNumberField"
        v-if="isFieldInfoTypeNumber"
        v-model="fieldSettings.value"
        @input="fieldUpdated" 
        @focus="$emit('valueFieldFocus', $event)"
        :note="fieldNote"
      />

      <FormInputText
        v-if="isFieldInfoTypeText"
        ref="inputTextField"
        v-model="fieldSettings.value"
        @input="fieldUpdated" 
        @focus="$emit('valueFieldFocus', $event)"
        :note="fieldNote"
      />

      <AutomationUserListField
        v-if="isFieldTypeUserList"
        :editable="true"
        v-model="fieldSettings.value"
        @input="fieldUpdated"
        @updateInfo="dynamicListFieldInfoUpdated"
        :note="fieldNote"
        />
    </FormRow>

    <FormRow v-if="!editing && hasFieldTypeSelected && hasFieldCompareValue" label="Values">
      <FormStaticLabel v-if="isFieldInfoTypeBoolean">{{formatLookupName(fieldSettings.value, fieldValueLookup)}}</FormStaticLabel>
      <FormStaticLabel v-if="isFieldInfoTypeEnum">{{formatMultiSelectLookupName(fieldSettings.value, fieldValueLookup)}}</FormStaticLabel>
      <FormStaticLabel v-if="isFieldInfoTypeNumber || isFieldInfoTypeText">{{fieldSettings.value}}</FormStaticLabel>
    </FormRow>

    <div v-if="!isLastCondition">
      <div class="text-center mb-4"><strong>AND</strong></div>
    </div>

  </div>
</template>


<script>
import _ from 'lodash'

import AutomationMixin from '@/modules/automations/mixins/automations'

import AutomationUserListField from '@/modules/automations/components/automationUserListField'
import AutomationDynamicListInput from '@/modules/automations/components/automationDynamicListInput'

import { AutomationFieldTypes, AutomationFieldCompareTypes, AutomationFieldCompareTypeLookup } from '@/store/types'

export default {
  name: 'AutomationConditionField',
  mixins: [
    AutomationMixin
  ],
  components: {
    AutomationDynamicListInput,
    AutomationUserListField
  },
  props: {
    label: String,
    value: Object,
    fieldTypeList: Array,
    editing: Boolean,
    isLastCondition: Boolean
  },
  watch: {
    value: function() {
      this.fieldSettings = _.cloneDeep(this.value || {})
    }
  },
  data() {
    return {
      fieldSettings: _.cloneDeep(this.value || {})
    }
  },
  computed: {
    hasFieldList() {
      return this.fieldTypeList && this.fieldTypeList.length > 0
    },
    isFieldModelNull() {
      if(this.selectedFieldInfo)
        return this.selectedFieldInfo.model == null
      return false
    },
    hasFieldTypeSelected() {
      return this.selectedFieldInfo != null
    },
    hasFieldCompareValue() {
      if(this.fieldSettings.compareType == AutomationFieldCompareTypes.isSet)
        return false
      return true
    },
    fieldTypeLookup() {
      if(this.fieldTypeList) {
        return this.fieldTypeList.map(fieldType => {
          let modelName = fieldType.modelName || fieldType.model
          var fieldName = `${this.capitalizeString(modelName)}${modelName ? ' - ' : ''}${fieldType.name}`

          // If model is same as field name, don't double up
          if(this.capitalizeString(modelName) == fieldType.name)
            fieldName = fieldType.name

          return {
            name: fieldName,
            value: `${fieldType.model}.${fieldType.field}`
          }
        })        
      }

      return []
    },
    fieldSettingKey() {
      let fieldType = this.selectedFieldInfo
      if(!fieldType)
        return null

      return `${fieldType.model}.${fieldType.field}`
    },
    selectedFieldInfo() {
      return this.fieldTypeList.find(fieldType => fieldType.field == this.fieldSettings.field && fieldType.model == this.fieldSettings.model)
    },
    selectedFieldType() {
      if(!this.selectedFieldInfo)
        return null
      return this.selectedFieldInfo.fieldType
    },
    fieldCompareLookup() {
      if(!this.selectedFieldInfo)
        return []

      return this.selectedFieldInfo.fieldCompareOptions.map(compareType => {
        return {
          name: this.formatLookupName(compareType, AutomationFieldCompareTypeLookup),
          value: compareType
        }        
      })
    },
    isFieldInfoTypeBoolean() {
      return this.selectedFieldType == AutomationFieldTypes.boolean
    },
    isFieldInfoTypeEnum() {
      return this.selectedFieldType == AutomationFieldTypes.enum
    },
    isFieldInfoTypeDynamicList() {
      return this.selectedFieldType == AutomationFieldTypes.dynamicList
    },
    isFieldInfoTypeNumber() {
      return this.selectedFieldType == AutomationFieldTypes.number
    },
    isFieldInfoTypeText() {
      return this.selectedFieldType == AutomationFieldTypes.text
    },
    isValueMultiSelect() {
      return this.isFieldInfoTypeEnum || this.isFieldInfoTypeDynamicList
    },
    isFieldTypeUserList() {
      return this.selectedFieldType == AutomationFieldTypes.userList
    },
    fieldValueLookup() {
      if(this.selectedFieldType == AutomationFieldTypes.enum) {
        return Object.keys(this.selectedFieldInfo.fieldEnumLookup).map(lookupKey => {
          return {
            name: this.selectedFieldInfo.fieldEnumLookup[lookupKey],
            value: lookupKey
          }
        })
      }

      if(this.selectedFieldType == AutomationFieldTypes.boolean) {
        return [ { name: 'True', value: 1 }, { name: 'False', value: 0 } ]
      }

      return []
    },
    fieldLinkType() {
      return this.selectedFieldInfo.fieldLinkType
    },
    fieldNote() {
      let fieldType = this.selectedFieldInfo
      if(!fieldType)
        return null

      return fieldType.note
    }
  },
  created() {
    this.AutomationFieldCompareTypeLookup = AutomationFieldCompareTypeLookup
  },
  methods: {
    fieldTypeUpdated(fieldKey) {
      let fieldLookup = this.fieldTypeList.find(fieldType => fieldKey == `${fieldType.model}.${fieldType.field}`)
      if(!fieldLookup)
        return

      // Reset compare / value
      this.fieldSettings.model = fieldLookup.model
      this.fieldSettings.field = fieldLookup.field

      if(fieldLookup.fieldCompareOptions.length > 0)
        this.fieldSettings.compareType = fieldLookup.fieldCompareOptions[0]

      if(this.isValueMultiSelect)
        this.fieldSettings.value = []
      else
        this.fieldSettings.value = ''

      this.fieldSettings.sourceValue = ''

      if(fieldLookup.sourceValueDefault)
        this.fieldSettings.sourceValue = fieldLookup.sourceValueDefault

      this.fieldUpdated()
    },
    fieldUpdated() {
      this.$emit('update', this.fieldSettings)
    },
    dynamicListFieldInfoUpdated(data) {
      this.fieldSettings.valueLookup = data || []
      this.fieldUpdated()
    },
    removeFieldClicked() {
      this.$emit('removeField')
    },
    insertTextAtCursor(insertText, isSourceField) {
      // Inserts into the input text / text area fields
      var inputComponent = null
      if(isSourceField) {
        inputComponent = this.$refs.inputSourceTextField
      } else {
        if(this.isFieldInfoTypeNumber)
          inputComponent = this.$refs.inputNumberField
        if(this.isFieldInfoTypeText)
          inputComponent = this.$refs.inputTextField
      }
      
      if(inputComponent) {
        inputComponent.insertTextAtCursor(insertText)
        inputComponent.focus()
      }
    }
  }
}
</script>